import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import {
  DpAccrualPeriod,
  listEmployeeVacationsApi,
  ACCRUAL_STATUS_BADGE,
  ACCRUAL_STATUS_LABELS,
  DpCalendarItem,
  DpExpiringPeriod,
  getVacationCalendarApi,
  hasVacationPayment,
  vacationPaymentBadge,
  getVacationsSummaryApi,
  listExpiringVacationsApi,
  VACATION_STATUS_BADGE,
  VACATION_STATUS_LABELS,
} from "../api/vacationsApi";
import { listCostCentersApi, DpCostCenter } from "../api/settingsApi";
import { formatDateOnly } from "../utils/format";
import EmployeePickerModal from "../components/EmployeePickerModal";
import { canSchedule, ScheduleModal } from "../components/EmployeeVacationsTab";
import type { DpEmployeeListItem } from "../api/employeesApi";
import { DP_EMPLOYEE_TAB_PAGE, DP_LEAVES_PAGE, DP_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const LIMIT = 20;
const WINDOWS = [30, 60, 90];
const UPCOMING_DAYS = 60;

type Tab = "vencimentos" | "proximas";

const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const addDaysIso = (iso: string, days: number) => {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * Visão do DP: períodos com saldo vencidos (férias em dobro) ou com concessivo
 * terminando na janela, e as férias programadas dos próximos 60 dias.
 */
export default function VacationsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  /** A aba fica na URL (`?aba=`, trocada com `replace`), como na ficha do colaborador: Voltar/F5 a preservam. */
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get("aba") === "proximas" ? "proximas" : "vencimentos";
  const setTab = (next: Tab) => setSearchParams(next === "vencimentos" ? {} : { aba: next }, { replace: true });

  const [days, setDays] = useState(60);
  const [costCenterId, setCostCenterId] = useState("");
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<DpExpiringPeriod[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<{ expired: number; expiring: number } | null>(null);
  const [upcoming, setUpcoming] = useState<DpCalendarItem[]>([]);

  // Programação a partir desta tela: colaborador → período com saldo → formulário.
  const [picking, setPicking] = useState(false);
  const [choosing, setChoosing] = useState<{ employee: DpEmployeeListItem; periods: DpAccrualPeriod[] } | null>(null);
  const [scheduling, setScheduling] = useState<DpAccrualPeriod | null>(null);
  /** Recarrega as próximas férias depois de uma programação feita aqui. */
  const [reloadKey, setReloadKey] = useState(0);

  const pickEmployee = async (employee: DpEmployeeListItem) => {
    if (!token) return;
    try {
      const periods = (await listEmployeeVacationsApi(token, employee.id)).filter(canSchedule);
      if (periods.length === 0) {
        toast.info(`${employee.socialName || employee.fullName} não tem período com saldo para programar.`);
        return;
      }
      setPicking(false);
      if (periods.length === 1) setScheduling(periods[0]);
      else setChoosing({ employee, periods });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as férias do colaborador.");
    }
  };

  useEffect(() => {
    if (!token) return;
    listCostCentersApi(token)
      .then(setCostCenters)
      .catch(() => setCostCenters([]));
    getVacationsSummaryApi(token)
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await listExpiringVacationsApi(token, {
        page,
        limit: LIMIT,
        days,
        costCenterId: costCenterId || undefined,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as férias.");
    } finally {
      setLoading(false);
    }
  }, [token, page, days, costCenterId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    const from = todayIso();
    getVacationCalendarApi(token, { from, to: addDaysIso(from, UPCOMING_DAYS), costCenterId: costCenterId || undefined })
      .then(setUpcoming)
      .catch(() => setUpcoming([]));
  }, [token, costCenterId, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Férias" }]}
            title="Férias"
            subtitle="Períodos vencidos (devidos em dobro), concessivos terminando e as próximas férias da equipe."
            onBack={goBack}
            backLabel="Voltar para o DP"
            actions={
              <>
                <button type="button" className="ecoBtn" onClick={() => navigate(DP_LEAVES_PAGE())}>
                  <EcoIcon name="activity" size={18} />
                  Afastamentos
                </button>
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setPicking(true)}>
                  <EcoIcon name="plus" size={18} />
                  Programar férias
                </button>
              </>
            }
          />

          {summary ? (
            <div className="dpCounters">
              <div className="dpCounter">
                <div className="dpCounter__value" style={summary.expired > 0 ? { color: "var(--eco-danger-ink)" } : undefined}>
                  {summary.expired}
                </div>
                <div className="dpCounter__label">Períodos vencidos com saldo</div>
              </div>
              <div className="dpCounter">
                <div className="dpCounter__value">{summary.expiring}</div>
                <div className="dpCounter__label">Concessivo vencendo em 60 dias</div>
              </div>
              <div className="dpCounter">
                <div className="dpCounter__value">{upcoming.length}</div>
                <div className="dpCounter__label">Férias programadas nos próximos {UPCOMING_DAYS} dias</div>
              </div>
            </div>
          ) : null}

          <nav className="dpTabs">
            <button
              className={`dpTab ${tab === "vencimentos" ? "is-active" : ""}`}
              onClick={() => setTab("vencimentos")}
            >
              A vencer e vencidos ({total})
            </button>
            <button
              className={`dpTab ${tab === "proximas" ? "is-active" : ""}`}
              onClick={() => setTab("proximas")}
            >
              Próximas férias ({upcoming.length})
            </button>
          </nav>

          <div className="dpControls">
            {tab === "vencimentos" ? (
              <select
                className="dpSelect"
                value={days}
                aria-label="Janela de vencimento"
                onChange={(e) => {
                  setPage(1);
                  setDays(Number(e.target.value));
                }}
              >
                {WINDOWS.map((w) => (
                  <option key={w} value={w}>
                    Vencidos + concessivo nos próximos {w} dias
                  </option>
                ))}
              </select>
            ) : null}
            <select
              className="dpSelect"
              value={costCenterId}
              aria-label="Filtrar por centro de custo"
              onChange={(e) => {
                setPage(1);
                setCostCenterId(e.target.value);
              }}
            >
              <option value="">Todos os centros de custo</option>
              {costCenters.map((cc) => (
                <option key={cc.id} value={cc.id}>
                  {cc.code} — {cc.name}
                </option>
              ))}
            </select>
          </div>

          {tab === "vencimentos" ? (
            <section className="dpSection">
              <div className="dpTableWrapper">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Colaborador</th>
                      <th>Período aquisitivo</th>
                      <th>Saldo</th>
                      <th>Conceder até</th>
                      <th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="dpEmpty">
                          Carregando...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="dpEmpty">
                          Nenhum período vencido ou a vencer nesta janela.
                        </td>
                      </tr>
                    ) : (
                      items.map((p) => (
                        <tr
                          key={p.id}
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(DP_EMPLOYEE_TAB_PAGE(p.employee.id, "ferias"))}
                          title="Abrir as férias do colaborador"
                        >
                          <td>
                            <strong>{p.employee.socialName || p.employee.fullName}</strong>
                          </td>
                          <td>
                            {p.sequence}º · {formatDateOnly(p.acquisitionStart)} a {formatDateOnly(p.acquisitionEnd)}
                          </td>
                          <td>{p.balance} dias</td>
                          <td>
                            {formatDateOnly(p.concessionEnd)}
                            <div className="dpMuted">
                              {p.daysToConcessionEnd < 0
                                ? `vencido há ${-p.daysToConcessionEnd} dia(s)`
                                : `em ${p.daysToConcessionEnd} dia(s)`}
                            </div>
                          </td>
                          <td>
                            <span className={`dpBadge ${ACCRUAL_STATUS_BADGE[p.status]}`}>
                              {ACCRUAL_STATUS_LABELS[p.status]}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="dpPagination">
                <span>
                  Mostrando {items.length} de {total} períodos
                </span>
                <div className="dpPagination__btns">
                  <button
                    className="dpBtn dpBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </button>
                  <span style={{ padding: "0 12px" }}>
                    Página {page} de {totalPages}
                  </span>
                  <button
                    className="dpBtn dpBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          {tab === "proximas" ? (
            <section className="dpSection">
              <p className="dpFormHint" style={{ marginTop: 0 }}>
                Férias programadas que começam ou estão em curso nos próximos {UPCOMING_DAYS} dias.
              </p>
              <div className="dpTableWrapper">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Colaborador</th>
                      <th>Gozo</th>
                      <th>Dias</th>
                      <th>Situação</th>
                      <th>Recibo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="dpEmpty">
                          Nenhuma férias programada no período.
                        </td>
                      </tr>
                    ) : (
                      upcoming.map((s) => (
                        <tr
                          key={s.id}
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(DP_EMPLOYEE_TAB_PAGE(s.employee.id, "ferias"))}
                        >
                          <td>
                            <strong>{s.employee.socialName || s.employee.fullName}</strong>
                          </td>
                          <td>
                            {formatDateOnly(s.startDate)} a {formatDateOnly(s.endDate)}
                            {s.status === "PLANEJADO" && s.daysToStart <= 30 ? (
                              <div className="dpMuted" style={{ color: "var(--eco-status-warn)" }}>
                                sem aviso registrado
                              </div>
                            ) : null}
                          </td>
                          <td>{s.days}</td>
                          <td>
                            <span className={`dpBadge ${VACATION_STATUS_BADGE[s.status]}`}>
                              {VACATION_STATUS_LABELS[s.status]}
                            </span>
                          </td>
                          <td>
                            {hasVacationPayment(s) ? (
                              <span className={`dpBadge ${vacationPaymentBadge(s.payment).className}`}>
                                {vacationPaymentBadge(s.payment).label}
                              </span>
                            ) : (
                              <span className="dpMuted">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </div>
      </main>

      {picking ? (
        <EmployeePickerModal
          token={token}
          title="Programar férias — escolha o colaborador"
          onClose={() => setPicking(false)}
          onPick={pickEmployee}
        />
      ) : null}

      {choosing ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {choosing.employee.socialName || choosing.employee.fullName} — qual período?
              </h2>
              <button className="dpModal__close" onClick={() => setChoosing(null)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <div className="dpTableWrapper">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th>Período aquisitivo</th>
                      <th>Saldo</th>
                      <th>Conceder até</th>
                    </tr>
                  </thead>
                  <tbody>
                    {choosing.periods.map((p) => (
                      <tr
                        key={p.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setChoosing(null);
                          setScheduling(p);
                        }}
                      >
                        <td>
                          {p.sequence}º · {formatDateOnly(p.acquisitionStart)} a {formatDateOnly(p.acquisitionEnd)}
                        </td>
                        <td>{p.balance} dias</td>
                        <td>
                          {formatDateOnly(p.concessionEnd)}{" "}
                          {p.status === "VENCIDO" ? <span className="dpBadge dpBadge--red">Vencido</span> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <footer className="dpModal__footer">
              <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setChoosing(null)}>
                Cancelar
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {scheduling ? (
        <ScheduleModal
          token={token}
          period={scheduling}
          onClose={() => setScheduling(null)}
          onSaved={() => {
            setScheduling(null);
            load();
            setReloadKey((k) => k + 1);
            getVacationsSummaryApi(token)
              .then(setSummary)
              .catch(() => setSummary(null));
          }}
        />
      ) : null}
    </div>
  );
}
